import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import useFetch from "../../../../hooks/useFetchMSAL";
import { TradeAndPromotionPlanningFetchAPI } from "../../../../models/APIResponses.model";
import PromotionPlanning from "../../../../models/TradeAndPromoPlanning/PromotionPlanning.model";
import { AG_GRID_MODULES, AG_GRID_OPTIONS, VOLUME_FORECAST_TAB_ID, PROMOTION_PLANNING_TAB_ID, TOAST_CONTAINER_ID, AG_GRID_DEFAULT_COL_DEF, FORECAST_REVIEW_TAB_ID, ANNUAL_SUMMARY_TAB_ID, TRADE_SPEND_REPORT_TAB_ID, SHORT_TERM_PROMOTION_SUB_TAB_ID } from "../../../../shared/constants";
import { usaNumberFormatter, getToastOptions, newGridRowStyleHandler, removeInsertedRowsById, createDescription } from "../../../../shared/functions";
import { RootState } from "../../../../store";
import { promotionPlanningTabActions } from "../../../../store/slices/promotion-planning-tab-slice";
import { tradeAndPromoPlanningTabActions } from "../../../../store/slices/trade-and-promo-planning-tab-slice";
import EditableGridActions from "../../../UI/EditableGridActions/EditableGridActions";
import { PromotionPlanningDropdownModel, PromotionPlanningSelectedSlicerModel } from "../../../../models/TradeAndPromoPlanning/PromotionPlanningSlicer.model";
import PromotionPlanningSlicers from "./PromotionPlanningSlicers";
import { PromotionDropdown, SKUDropdown } from "../../../../models/TradeAndPromoPlanning/PromotionPlanningGridDropdown.model";
import { IRichCellEditorParams } from "ag-grid-community";
import { MdCalendarMonth } from "react-icons/md";
import { createPortal } from "react-dom";
import Modal from "../../../UI/Modal/Modal";
import ChangeLogTable from "../../../UI/ChangeLog/ChangeLogTable";

interface GridDropdownData {
    promotions: PromotionDropdown[];
    sku: SKUDropdown[];
};

const gridColumns: {
    field: string;
    label: string;
    dataType: "text" | "number" | "dateString";
    width: number;
}[] = [
    { field: "customer_name", label: "Customer", dataType: "text", width: 150 },
    { field: "sku_name", label: "Product", dataType: "text", width: 150 },
    { field: "promotion", label: "Promotion Name", dataType: "text", width: 150 },
    { field: "promotion_calendar_id", label: "Promotion Calendar ID", dataType: "text", width: 150 },
    { field: "week_start_date", label: "Week Start Date", dataType: "dateString", width: 150 },
    { field: "week_end_date", label: "Week End Date", dataType: "dateString", width: 150 },
    { field: "everyday_price_per_case", label: "Long Term Price/Case ($)", dataType: "number", width: 150 },
    { field: "final_promotional_price", label: "Final Promotional Price/Case ($)", dataType: "number", width: 150 },
    { field: "everyday_baseline_volume", label: "Everyday Baseline Volume", dataType: "number", width: 150 },
    { field: "incremental_case_volume", label: "Incremental Volume", dataType: "number", width: 150 },
    { field: "stat_forecast", label: "Stat Forecast", dataType: "number", width: 150 },
    { field: "final_consumption_forecast", label: "Final Consumption Forecast", dataType: "number", width: 150 },
    { field: "take_rate", label: "Take Rate (%)", dataType: "number", width: 150 },
    { field: "scan_volume", label: "Scan Volume", dataType: "number", width: 150 },
    { field: "scan_rate_per_case", label: "Scan Rate / Case ($)", dataType: "number", width: 150 },
    { field: "non_scan_rate_per_case", label: "Non-scan Rate / Case ($)", dataType: "number", width: 150 },
    { field: "oi_rate_per_case", label: "OI Rate / Case ($)", dataType: "number", width: 150 },
    { field: "bb_direct_rate_per_case", label: "BB Direct Rate / Case ($)", dataType: "number", width: 150 },
    { field: "feature_fixed", label: "Feature Fixed ($)", dataType: "number", width: 150 },
    { field: "coupon_fixed", label: "Coupon Fixed ($)", dataType: "number", width: 150 },
    { field: "display_fixed", label: "Display Fixed ($)", dataType: "number", width: 150 },
    { field: "total_fixed_per_case", label: "Total Fixed / Case ($)", dataType: "number", width: 150 },
    { field: "promotion_retail_price", label: "Special Retail Price ($)", dataType: "number", width: 150 },
    { field: "trade_spend", label: "Trade Spend ($)", dataType: "number", width: 150 },
    { field: "trade_spend_ratio", label: "T:S Ratio (%)", dataType: "number", width: 150 }
];

const PromotionPlanningGrid = (props: {
    gridHeight: string;
}) => {
    // States
    const [gridRowData, setGridRowData] = useState<any[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<any[]>([]);
    const [gridColDef, setGridColDef] = useState<any[]>([]);
    const [gridDropdownData, setGridDropdownData] = useState<GridDropdownData>({
        promotions: [],
        sku: []
    });
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const [newRowCount, setNewRowCount] = useState<number>(1);
    const [changeTrackState, setChangeTrackState] = useState(false);
    const [insertedRows, setInsertedRows] = useState<any>({});
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [selectedRowData, setSelectedRowData] = useState<any>([]);
    const [enableDelButton, setEnableDelButton] = useState(false);
    const [editedColIds, setEditedColIds] = useState<any[]>([]);
    const [gridCellEdits, setGridCellEdits] = useState<{
        [key: string]: {
            [key: string]: string | number;
        };
    }>({});
    const [changeLogData, setChangeLogData] = useState([]);
    const [changeLogDataFetched, setChangeLogDataFetched] = useState(false);

    // Variables
    const agGridRef = useRef<AgGridReact>(null);
    const [, fetchData] = useFetch([]);

    // Redux variables
    const dispatchFn = useDispatch();
    const globalCustomer: { id: number; name: string; } = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.globalCustomer);
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.tabApiDataFetched)[PROMOTION_PLANNING_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.tradeAndPromoPlanningTab.activeSubTabId);
    const selectedSlicers: PromotionPlanningSelectedSlicerModel = useSelector((state: RootState) => state.promotionPlanning.selectedSlicers);
    /**
   * Deletes selected rows
   * @param {any[]} selectedRowIds Selected Row IDs
   */
    const deleteSelectedRowData = async (selectedRowIds: any[], data1:any) => {
        let deleteToastID;

        try {
            // Delete selected newly inserted rows
            const oldDataRowIds: any[] = selectedRowIds.filter(rowId => !rowId.toString().startsWith("new_"));
            setInsertedRows((insertedRows: any) => removeInsertedRowsById(insertedRows, selectedRowIds));

            if (oldDataRowIds.length) {
                deleteToastID = toast.loading("Deleting selected rows...", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("loading"),
                });

                const deleteAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                    result: {
                        data: PromotionPlanning[];
                    },
                    slicers: PromotionPlanningDropdownModel;
                }>> = await fetchData('/promotion-planning', {
                    method: 'DELETE',
                    data: {
                        promotion_calendar_row_ids: oldDataRowIds.map(id => Number(id)),
                        row_data: selectedRowData,
                        filters: {
                            customer_id: globalCustomer.id,
                            sku_ids: selectedSlicers.sku_ids,
                            year: selectedSlicers.year
                        },
                        description: createDescription(PROMOTION_PLANNING_TAB_ID, "delete", selectedRowData),
                        tabname: PROMOTION_PLANNING_TAB_ID,
                        ...data1
                    }
                });

                toast.dismiss({
                    id: deleteToastID,
                    containerId: TOAST_CONTAINER_ID,
                });

                if (deleteAPIResponse.data.data) {
                    const apiRowData: PromotionPlanning[] = deleteAPIResponse.data.data.result.data;

                    setGridCellEdits({});
                    setInsertedRows({});

                    setGridRowData(structuredClone(apiRowData));
                    setGridRowDataCopy(structuredClone(apiRowData));

                    toast.success("Selected rows deleted successfully", {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("success"),
                    });

                    dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                        [VOLUME_FORECAST_TAB_ID]: false,
                        [PROMOTION_PLANNING_TAB_ID]: true,
                        [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
                        [FORECAST_REVIEW_TAB_ID]: false,
                        [ANNUAL_SUMMARY_TAB_ID]: false,
                        [TRADE_SPEND_REPORT_TAB_ID]: false
                    }));

                } else {
                    toast.error(deleteAPIResponse.data.error, {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("error"),
                    });
                }
            } else {
                const selectedRows: any[] | undefined = agGridRef.current?.api.getSelectedRows();
                agGridRef.current?.api.applyTransaction({
                    remove: selectedRows
                });
            }
        } catch (error: any) {
            toast.error("Failed to delete the selected rows", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    };

    /**
     * Returns ColDef for Promotion Planning data grid
     * @param {GridDropdownData} gridDropdownData Grid dropdown data from POST /promotion-planning API
     * @returns {any[]} Generated ColDef for Promotion Planning data grid
     */
    const generateColDef = useCallback((gridDropdownData: GridDropdownData): any[] => {
        return [
            {
                headerTooltip: "ID",
                cellDataType: "text",
                headerName: "ID",
                width: 120,
                headerCheckboxSelection: true,
                checkboxSelection: true,
                showDisabledCheckboxes: true,
                valueGetter: (params: any) => (params.data.promotion_id) ? `TP-${params.data.promotion_id}` : "",
                tooltipValueGetter: (params: any) => params.value,
                pinned: "left"
            },
            ...gridColumns.map((col, index) => {
                return {
                    ...((index >= 6) ? {
                        colId: col.field,
                        minWidth: 100
                    } : {}),
                    field: col.field,
                    headerTooltip: col.label,
                    cellDataType: col.dataType,
                    cellClass: col.dataType === "text" ? "ag-left-aligned-cell" : "ag-right-aligned-cell",
                    headerName: col.label,
                    ...((col.dataType === "number") ? {
                        cellClass: "ag-right-aligned-cell",
                        headerClass: "ag-left-aligned-cell",
                    } : {}),
                    ...((index <= 2) ? { pinned: "left" } : {}),
                    hide: (col.label === "Promotion Calendar ID"),
                    tooltipValueGetter: (params: any) => params.valueFormatted,
                    editable: (params: any) => {
                        if (params.data.id && (params.data.id as string).startsWith("new_")) {
                            const isSKUPresent: boolean = params.data.sku_name;
                            const isPromotionPresent: boolean = params.data.promotion;
                            const isWeekStartDatePresent: boolean = params.data.week_start_date;

                            return (
                                col.label === "Product" ||
                                (col.label === "Promotion Name" && isSKUPresent) ||
                                (col.label === "Week Start Date" && isPromotionPresent) ||
                                (col.label === "Week End Date" && isWeekStartDatePresent)
                            );
                        }

                        return (
                            // col.label === "Promotion Name" ||
                            col.label === "Week Start Date" ||
                            col.label === "Week End Date"
                        );
                    },
                    cellEditorSelector: (params: any) => {
                        if (
                            col.label === "Customer" ||
                            col.label === "Product" ||
                            col.label === "Promotion Name"
                        ) {
                            let valueList: string[] = [];
                            switch (col.label) {
                                case "Product":
                                    valueList = gridDropdownData.sku.map(s => s.sku_name);
                                    break;

                                case "Promotion Name":
                                    const rowSkuID: number = gridDropdownData.sku.find(s => s.sku_name === params.data.sku_name)?.sku_id as number;
                                    valueList = [...gridDropdownData.promotions.filter(p => p.customer_id == globalCustomer.id && p.sku_id == rowSkuID).map(p => p.promotion_name)];

                                    if (valueList.length === 0) {
                                        toast.error(`No promotion(s) available for Customer: ${params.data.customer_name} and Product: ${params.data.sku_name}.`, {
                                            containerId: TOAST_CONTAINER_ID,
                                            ...getToastOptions("error")
                                        });
                                    }

                                    break;
                            }

                            return {
                                component: "agRichSelectCellEditor",
                                params: {
                                    values: valueList,
                                    valueListMaxWidth: "320px",
                                    searchType: "match",
                                    allowTyping: true,
                                    filterList: true,
                                    highlightMatch: true
                                } as IRichCellEditorParams
                            };
                        } else if (
                            col.label === "Week Start Date" ||
                            col.label === "Week End Date"
                        ) {
                            return {
                                component: "agDateStringCellEditor",
                            };
                        }

                        return {
                            component: "agTextCellEditor"
                        };
                    },
                    valueFormatter: (params: any): any => {
                        if (typeof params.value === "number") {
                            const precision: number = (
                                [
                                    "final_promotional_price",
                                    "everyday_price_per_case",
                                    "take_rate",
                                    "scan_rate_per_case",
                                    "non_scan_rate_per_case",
                                    "oi_rate_per_case",
                                    "bb_direct_rate_per_case",
                                    "feature_fixed",
                                    "coupon_fixed",
                                    "display_fixed",
                                    "total_fixed_per_case",
                                    "promotion_retail_price",
                                    "trade_spend_ratio"
                                ].includes(col.field)
                            ) ? 2 : 0;

                            const numStr: string | null = usaNumberFormatter(
                                (col.field === "trade_spend_ratio" || col.field === "take_rate") ? params.value * 100 : params.value,
                                precision
                            );
                            return numStr ? (
                                col.label.includes("$") ? `$${numStr}` : (
                                    (col.field === "trade_spend_ratio" || col.field === "take_rate") ? `${numStr}%` : numStr
                                )
                            ) : numStr;
                        }

                        return params.value;
                    },
                    cellRenderer: (params: any) => {
                        if (col.label === 'Week Start Date' || col.label === 'Week End Date') {
                            return params.value || <span><MdCalendarMonth /></span>
                        }
                        if (typeof params.value === "number") {
                            const precision: number = (
                                [
                                    "final_promotional_price",
                                    "everyday_price_per_case",
                                    "take_rate",
                                    "scan_rate_per_case",
                                    "non_scan_rate_per_case",
                                    "oi_rate_per_case",
                                    "bb_direct_rate_per_case",
                                    "feature_fixed",
                                    "coupon_fixed",
                                    "display_fixed",
                                    "total_fixed_per_case",
                                    "promotion_retail_price",
                                    "trade_spend_ratio"
                                ].includes(col.field)
                            ) ? 2 : 0;

                            const numStr: string | null = usaNumberFormatter(
                                (col.field === "trade_spend_ratio" || col.field === "take_rate") ? params.value * 100 : params.value,
                                precision
                            );
                            return numStr ? (
                                col.label.includes("$") ? `$${numStr}` : (
                                    (col.field === "trade_spend_ratio" || col.field === "take_rate") ? `${numStr}%` : numStr
                                )
                            ) : numStr;
                        }
                        return params.value
                    }
                };
            })
        ];
    }, [globalCustomer.id]);

    /**
     * Async function to fetch data from POST /promotion-planning API
     */
    const fetchPromotionPlanningData = useCallback(async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching Promotion Planning data...', {

            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: PromotionPlanning[];
                    dropdown_data: {
                        promotions: PromotionDropdown[];
                        sku: SKUDropdown[];
                    };
                },
                slicers: PromotionPlanningDropdownModel;
            }>> = await fetchData("/promotion-planning", {
                method: "POST",
                data: {
                    filters: {
                        customer_id: globalCustomer.id,
                        sku_ids: selectedSlicers.sku_ids,
                        year: selectedSlicers.year
                    }
                }
            });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (fetchAPIResponse.data.data) {
                const apiGridData: PromotionPlanning[] = fetchAPIResponse.data.data.result.data;
                const apiDropdownData: GridDropdownData = fetchAPIResponse.data.data.result.dropdown_data;

                setGridCellEdits({});
                setInsertedRows({});
                setGridRowData(structuredClone(apiGridData));
                setGridRowDataCopy(structuredClone(apiGridData));
                setGridColDef((prevColDef) => generateColDef(apiDropdownData));
                setGridDropdownData(structuredClone(apiDropdownData));

                dispatchFn(promotionPlanningTabActions.setSlicers(structuredClone(fetchAPIResponse.data.data.slicers)));
                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [PROMOTION_PLANNING_TAB_ID]: true
                }));
                setChangeLogDataFetched(true);

            } else {
                toast.error('Error in fetching Promotion Planning data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Promotion Planning data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [globalCustomer.id, selectedSlicers, dispatchFn, fetchData, generateColDef]);

    /**
     * Returns the validity of 'Week Start Date' and 'Week End Date' fields.
     * @param {Date} weekStartDateObj Week Start Date object.
     * @param {Date} weekEndDateObj Week End Date object.
     * @returns {boolean} Returns true if week start and end dates are valid, else false.
     */
    const areWeekStartAndEndDateValid = (weekStartDateObj: Date, weekEndDateObj: Date): boolean => {
        const isWeekStartOnSunday: boolean = weekStartDateObj.getUTCDay() === 0;

        if (!isWeekStartOnSunday) {
            toast.error("Week Start Date should be Sunday", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error")
            });
            return false;
        }

        const isWeekEndOnSaturday: boolean = weekEndDateObj.getUTCDay() === 6;

        if (!isWeekEndOnSaturday) {
            toast.error("Week End Date should be Saturday", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error")
            });
            return false;
        }

        const isWeekEndAfterWeekStart: boolean = weekEndDateObj > weekStartDateObj;

        if (!isWeekEndAfterWeekStart) {
            toast.error("Week End Date should be after Week Start Date", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error")
            });
            return false;
        }

        const daysBetweenWeekStartAndWeekEnd: number = Math.round((weekEndDateObj.getTime() - weekStartDateObj.getTime()) / (1000 * 3600 * 24));
        const is6DaysBetweenWeekStartAndWeekEnd: boolean = daysBetweenWeekStartAndWeekEnd >= 6;

        if (!is6DaysBetweenWeekStartAndWeekEnd) {
            toast.error("Minimum 6 days of gap is mandatory between Week Start and End Date", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error")
            });
            return false;
        }

        return true;
    };

    /**
     * Replaces updated cell value with replace value in params and returns the updated params.
     * @param {any} params Cell value change params.
     * @param {any} replaceValue Value to replace updated cell value.
     * @returns {any} Updated params.
     */
    const replaceCellUpdateDataInParams = (params: any, replaceValue: any): any => {
        const newParams: any = { ...params };
        const colName: string = params.column.colId;

        params.data[colName] = replaceValue;
        params.newValue = replaceValue;
        params.value = replaceValue;

        params.api.refreshCells({
            force: true,
            columns: [params.column.getId()],
            rowNodes: [params.node]
        });

        return newParams;
    };
    /**
     * Event handler for cell value change in AG Grid
     * @param {any} params Cell value change event params
     */
    const cellValueChangeHandler = (params: any) => {
        var rowId = params.node.data.id;
        const colName = params.column.colId;

        if (rowId && rowId.toString().startsWith("new_")) {
            setInsertedRows((prev: any) => {
                let newData = prev;
                let newRow = newData[rowId] ? newData[rowId] : {};

                if (colName === "week_start_date") {
                    const weekStartDateObj: Date = new Date(params.newValue);
                    const isWeekStartOnSunday: boolean = weekStartDateObj.getUTCDay() === 0;

                    // if (weekStartDateObj.getUTCFullYear() !== selectedSlicers.year) {
                    //     toast.error(`Week Start Date's year must be same as that of the selected year ${selectedSlicers.year}`, {
                    //         containerId: TOAST_CONTAINER_ID,
                    //         ...getToastOptions("error")
                    //     });
                    //     return newData;
                    // }

                    if (!isWeekStartOnSunday) {
                        toast.error("Week Start Date should be Sunday", {
                            containerId: TOAST_CONTAINER_ID,
                            ...getToastOptions("error")
                        });

                        params = replaceCellUpdateDataInParams(params, params.oldValue || undefined);

                        return newData;
                    }
                }

                if (colName === "week_end_date") {
                    const weekStartDateObj: Date = new Date(params.data.week_start_date);
                    const weekEndDateObj: Date = new Date(params.newValue);

                    // Check validity of 'Week Start Date' and 'Week End Date' fields
                    const weekStartAndEndDateValid: boolean = areWeekStartAndEndDateValid(weekStartDateObj, weekEndDateObj);

                    if (!weekStartAndEndDateValid) {
                        params = replaceCellUpdateDataInParams(params, params.oldValue || undefined);
                        return newData;
                    }
                }

                newRow[colName] = params.newValue;
                newData[rowId] = newRow;
                return newData;
            });
        } else if ((params.oldValue !== params.newValue)) {
            if (colName === "week_start_date" || colName === "week_end_date") {
                const weekStartDateObj: Date = new Date(params.data.week_start_date);
                const weekEndDateObj: Date = new Date(params.data.week_end_date);

                // if (colName === "week_start_date") {
                //     const oldWeekStartDateObj: Date = new Date(params.oldValue);

                //     if (weekStartDateObj.getUTCFullYear() !== oldWeekStartDateObj.getUTCFullYear()) {
                //         toast.error(`Week Start Date year should be same as that of old value`, {
                //             containerId: TOAST_CONTAINER_ID,
                //             ...getToastOptions("error")
                //         });
                //         params = replaceCellUpdateDataInParams(params, params.oldValue);
                //         return;
                //     }
                // }

                // Check validity of 'Week Start Date' and 'Week End Date' fields
                const weekStartAndEndDateValid: boolean = areWeekStartAndEndDateValid(weekStartDateObj, weekEndDateObj);

                if (!weekStartAndEndDateValid) {
                    params = replaceCellUpdateDataInParams(params, params.oldValue);
                    return;
                }
            }

            params.colDef.cellStyle = (p: any) => (
                (p.rowIndex.toString() === params.node.id) ? { backgroundColor: '#8af38a' } : {}
            );

            params.api.refreshCells({
                force: true,
                columns: [params.column.getId()],
                rowNodes: [params.node]
            });

            if (editedColIds.indexOf(params.column.getId()) === -1) {
                setEditedColIds((prev) => [...prev, params.column.getId()]);
            }

            // check if newly added row is edited or not.
            // checking if current data is present in grid data or not
            const isPresent = gridRowData.some(obj => {
                return params.data.promotion_calendar_id === obj.promotion_calendar_id
            }
            );

            setChangeTrackState(true);

            if (isPresent) {
                setGridCellEdits((cellEdits) => {
                    const promoCalendarID: string = params.data.promotion_calendar_id;

                    if (cellEdits[promoCalendarID]) {
                        return {
                            ...cellEdits,
                            [promoCalendarID]: {
                                ...cellEdits[promoCalendarID],
                                [colName]: params.newValue
                            }
                        };
                    }

                    return {
                        ...cellEdits,
                        [promoCalendarID]: {
                            "customer_id": params.data.customer_id,
                            "sku_id": params.data.sku_id,
                            "week_start_date": params.data.week_start_date,
                            "week_end_date": params.data.week_end_date,
                            "promotion": params.data.promotion,
                            [colName]: params.newValue
                        }
                    };
                });
            } else {
                setInsertedRows((prev: any) => {
                    const promoCalendarID: number = parseInt(params.data.promotion_calendar_id);
                    let changes = prev;
                    changes[`new_${Math.abs(promoCalendarID)}`] = {
                        // "customer_name": params.data.customer_name,
                        "sku_name": params.data.sku_name,
                        "week_start_date": params.data.week_start_date,
                        "week_end_date": params.data.week_end_date,
                        "promotion": params.data.promotion,
                        [colName]: params.newValue
                    };
                    return changes

                });
            }

        }
    }

    /**
    * Event handler for row selection
    * @param {any} event Row selection event
    */
    const rowSelectionHandler = (event: any): void => {
        const selectedRows = agGridRef.current!.api!.getSelectedRows();
        setSelectedRowData(selectedRows);
        const selectedRowIds: any[] = selectedRows.map((row) => row.id ? row.id : row.promotion_calendar_id.toString());
        setSelectedRowIds([...selectedRowIds]);
    };

    /**
       * Updates cell values by using Promotion Planning update API
       * @param {any} gridCellEdits Cell edits in the grid
       * @param {any} insertedRows Newly inserted rows
       */
    const updateCellValues = useCallback(async (
        gridCellEdits: any,
        insertedRows: any,
        data:any
    ): Promise<void> => {
        try {
            const updateToastID = toast.loading("Updating Promotion Planning data...", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("loading"),
            });

            const updateAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: PromotionPlanning[];
                },
                slicers: PromotionPlanningDropdownModel;
            }>> = await fetchData("/promotion-planning/update-or-insert", {
                method: "POST",
                data: {
                    filters: {
                        customer_id: globalCustomer.id,
                        sku_ids: selectedSlicers.sku_ids,
                        year: selectedSlicers.year
                    },
                    update_data: gridCellEdits,
                    insert_data: insertedRows,
                    description: createDescription(PROMOTION_PLANNING_TAB_ID, "update", {...gridCellEdits, ...insertedRows}, {...gridRowData}),
                    tabname: PROMOTION_PLANNING_TAB_ID,
                    ...data
                }
            });

            toast.dismiss({
                id: updateToastID,
                containerId: TOAST_CONTAINER_ID,
            });

            if (updateAPIResponse.data.data) {
                const apiGridData: PromotionPlanning[] = updateAPIResponse.data.data.result.data;

                setGridCellEdits({});
                setInsertedRows({});

                setGridRowData(structuredClone(apiGridData));
                setGridRowDataCopy(structuredClone(apiGridData));

                dispatchFn(promotionPlanningTabActions.setSlicers(structuredClone(updateAPIResponse.data.data.slicers)));
                dispatchFn(tradeAndPromoPlanningTabActions.setTabApiDataFetched({
                    [VOLUME_FORECAST_TAB_ID]: false,
                    [PROMOTION_PLANNING_TAB_ID]: true,
                    [SHORT_TERM_PROMOTION_SUB_TAB_ID]: false,
                    [FORECAST_REVIEW_TAB_ID]: false,
                    [ANNUAL_SUMMARY_TAB_ID]: false,
                    [TRADE_SPEND_REPORT_TAB_ID]: false
                }));
                setChangeLogDataFetched(true);

                toast.success("Promotion Planning data updated successfully", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success"),
                });

            } else if (updateAPIResponse.data.error) {
                toast.error(updateAPIResponse.data.error, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || "Error in updating the Promotion Planning data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    }, [globalCustomer.id, selectedSlicers, fetchData, dispatchFn, gridRowData]);

    /**
       * Inserts a new row into the grid
       */
    const addRowHandler = (): void => {
        const newRowId: string = `new_${newRowCount.toString()}`;
        agGridRef.current!.api!.applyTransaction({
            add: [{ id: newRowId, customer_name: globalCustomer.name }],
            addIndex: 0,
        });
        setInsertedRows((insertedRows: any) => {
            return {
                ...insertedRows,
                [newRowId]: {}
            };
        });
        setChangeTrackState(true);
        setNewRowCount((prev) => prev + 1);
    };

    /**
   * Deletes selected rows from the grid
   */
    const deleteRowHandler = (e:any, data:any): void => {
        deleteSelectedRowData(selectedRowIds, data);
        setSelectedRowIds([]);
        setEnableDelButton(false);
    };

    /**
       * Event handler for reset button click
       * @param {any} params Reset button click event data
       */
    const resetCellEditsHandler = async (params: any): Promise<void> => {
        try {
            const resetConsent: boolean = window.confirm(
                "Do you want to reset the changes done to the grid?"
            );

            if (resetConsent) {
                setGridCellEdits({});
                setInsertedRows({});
                setChangeTrackState(false);
                setGridRowDataCopy(structuredClone([...gridRowData]));
            }
        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    };

    /**
   * Event handler for save button click
   * @param {any} event Save button click event data
   */
    const saveCellEditsHandler = useCallback((event: any, data:any={}): any => {
        try {
            const updatedInsertedRowData: any = {};
            const updatedCellEditData: any = {};
            const colMapping: any = {
                "promotion": "Promotion ID",
                "customer_id": "Customer Id",
                "sku_id": "Product_At_Volume_Id",
                "week_start_date": "Week Start Date",
                "week_end_date": "Week End Date"
            };

            // Modify inserted row data
            const requiredNewRowFields: string[] = ["sku_name", "promotion", "week_start_date", "week_end_date"];
            Object.keys(insertedRows).forEach((newRowID) => {
                if (
                    Object.keys(insertedRows[newRowID]).toSorted().toString() !== requiredNewRowFields.toSorted().toString()
                ) {
                    toast.error("New row(s) have invalid data. Kindly fill the required data in new row(s).", {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("error")
                    });
                    return;
                }

                const skuID: number = gridDropdownData.sku.find(s => s.sku_name === insertedRows[newRowID]["sku_name"])?.sku_id as number;
                const promoID: number = gridDropdownData.promotions.find(p => p.promotion_name === insertedRows[newRowID]["promotion"])?.promotion_id as number;

                updatedInsertedRowData[newRowID] = {
                    [colMapping["customer_id"]]: globalCustomer.id,
                    [colMapping["sku_id"]]: skuID,
                    [colMapping["promotion"]]: promoID,
                    [colMapping["week_start_date"]]: insertedRows[newRowID]["week_start_date"],
                    [colMapping["week_end_date"]]: insertedRows[newRowID]["week_end_date"]
                };
            });

            // Modify cell edit data
            Object.keys(gridCellEdits).forEach((promoID) => {
                updatedCellEditData[promoID] = {};
                Object.keys(gridCellEdits[promoID]).forEach((key) => {
                    if (key === "promotion") {
                        const customerID: number = gridCellEdits[promoID]["customer_id"] as number;
                        const skuID: number = gridCellEdits[promoID]["sku_id"] as number;
                        const selectedPromoName: string = gridCellEdits[promoID]["promotion"] as string;
                        const selectedPromoID = gridDropdownData.promotions.filter(
                            p => p.customer_id === customerID && p.sku_id === skuID && p.promotion_name === selectedPromoName
                        )[0].promotion_id;
                        updatedCellEditData[promoID][colMapping[key]] = selectedPromoID;

                    } else {
                        updatedCellEditData[promoID][colMapping[key]] = gridCellEdits[promoID][key];
                    }
                });
            });

            if (
                Object.keys(updatedCellEditData).length ||
                Object.keys(updatedInsertedRowData).length
            ) {
                updateCellValues(updatedCellEditData, updatedInsertedRowData, data);
                return true;
            }else{
                return false;
            }
        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    }, [globalCustomer.id, gridDropdownData, gridCellEdits, insertedRows, updateCellValues]);


    /**
   * Event handler for save button click
   * @param {any} event Save button click event data
   */
    const recalculateHandler = useCallback((event: any): void => {
        try {
            const updatedInsertedRowData: any = {};
            const updatedCellEditData: any = {};
            const colMapping: any = {
                "promotion": "Promotion ID",
                "customer_id": "Customer Id",
                "sku_id": "Product_At_Volume_Id",
                "week_start_date": "Week Start Date",
                "week_end_date": "Week End Date"
            };

            // Modify inserted row data
            const requiredNewRowFields: string[] = ["sku_name", "promotion", "week_start_date", "week_end_date"];
            Object.keys(insertedRows).forEach((newRowID) => {
                if (
                    Object.keys(insertedRows[newRowID]).toSorted().toString() !== requiredNewRowFields.toSorted().toString()
                ) {
                    toast.error("New row(s) have invalid data. Kindly fill the required data in new row(s).", {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("error")
                    });
                    return;
                }

                const skuID: number = gridDropdownData.sku.find(s => s.sku_name === insertedRows[newRowID]["sku_name"])?.sku_id as number;
                const promoID: number = gridDropdownData.promotions.find(p => p.promotion_name === insertedRows[newRowID]["promotion"])?.promotion_id as number;

                updatedInsertedRowData[newRowID] = {
                    [colMapping["customer_id"]]: globalCustomer.id,
                    [colMapping["sku_id"]]: skuID,
                    [colMapping["promotion"]]: promoID,
                    [colMapping["week_start_date"]]: insertedRows[newRowID]["week_start_date"],
                    [colMapping["week_end_date"]]: insertedRows[newRowID]["week_end_date"],
                    promotion_type: insertedRows[newRowID]["promotion"].includes('ED') ? 'Everyday' : 'Promotion'
                };
            });

            // Modify cell edit data
            Object.keys(gridCellEdits).forEach((promoID) => {
                updatedCellEditData[promoID] = {};
                Object.keys(gridCellEdits[promoID]).forEach((key) => {
                    if (key === "promotion") {
                        const customerID: number = gridCellEdits[promoID]["customer_id"] as number;
                        const skuID: number = gridCellEdits[promoID]["sku_id"] as number;
                        const selectedPromoName: string = gridCellEdits[promoID]["promotion"] as string;
                        const selectedPromoID = gridDropdownData.promotions.filter(
                            p => p.customer_id === customerID && p.sku_id === skuID && p.promotion_name === selectedPromoName
                        )[0].promotion_id;
                        updatedCellEditData[promoID][colMapping[key]] = selectedPromoID;

                    } else {
                        updatedCellEditData[promoID][colMapping[key]] = gridCellEdits[promoID][key];
                    }
                });
            });

            if (
                Object.keys(updatedCellEditData).length ||
                Object.keys(updatedInsertedRowData).length
            ) {
                fetchRecalculatedData(updatedCellEditData, updatedInsertedRowData);
            }
        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    }, [globalCustomer.id, gridDropdownData, gridCellEdits, insertedRows, updateCellValues]);

    /**
       * Event handler for reset button click
       * @param {any} params Reset button click event data
       */
    const fetchRecalculatedData = useCallback(async (updatedCellEditData: any, updatedInsertedRowData: any) => {
        try {
            const updateToastID = toast.loading("Recalculating Promotion Planning data...", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("loading"),
            });

            const updateAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<{
                result: {
                    data: PromotionPlanning[];
                },
                slicers: PromotionPlanningDropdownModel;
            }>> = await fetchData("/promotion-planning/recalculate", {
                method: "POST",
                data: {
                    filters: {
                        customer_id: globalCustomer.id,
                        sku_ids: selectedSlicers.sku_ids,
                        year: selectedSlicers.year
                    },
                    update_data: updatedCellEditData,
                    insert_data: updatedInsertedRowData
                }
            });

            toast.dismiss({
                id: updateToastID,
                containerId: TOAST_CONTAINER_ID,
            });
            if (updateAPIResponse.data.data) {
                const apiGridData: any = updateAPIResponse.data.data.result;

                setGridRowDataCopy(structuredClone(apiGridData));

                toast.success("Promotion Planning data recalculated successfully", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success"),
                });

            } else if (updateAPIResponse.data.error) {
                toast.error(updateAPIResponse.data.error, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error?.message || "Error in recalculating the Promotion Planning data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    }, [globalCustomer.id, selectedSlicers, fetchData, dispatchFn]);

    /**
     * Async function to fetch data from GET /change-log
     */
    const fetchChangeLogData = async (): Promise<void> => {
        const fetchingDataToastID = toast.loading('Fetching change log data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<TradeAndPromotionPlanningFetchAPI<any>> = await fetchData("/get-change-log", {
                method: "GET",
                params: {
                    tabName: activeSubTabId
                    }
            });

            if (fetchAPIResponse.data.data) {
                setChangeLogData(fetchAPIResponse.data.data.result);
                setChangeLogDataFetched(false);

            } else {
                toast.error('Error in fetching Change Log data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

        } catch (error: Error | any) {
            console.error("Error ", error)
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching Change Log data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };

    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeSubTabId === PROMOTION_PLANNING_TAB_ID
        ) {
            fetchPromotionPlanningData();
        }
    }, [selectedSlicers, tabApiDataFetched, activeSubTabId, fetchPromotionPlanningData]);

    useEffect(() => {
        setEnableDelButton(selectedRowIds.length > 0);
    }, [selectedRowIds]);


    useEffect(() => {
        if (
            Object.keys(insertedRows).length === 0 &&
            Object.keys(gridCellEdits).length === 0
        ) {
            setChangeTrackState(false);
        }
    }, [insertedRows, gridCellEdits]);

    return (
        <>
            <div className="row">
                <div className="col col-8 text-start">
                    <PromotionPlanningSlicers isGridChanged={changeTrackState} />
                </div>

                <div className="col col-4 text-end">
                    <div className="d-flex justify-content-end">
                        <div className="d-inline mx-1">
                            <button className="btn btn-warning btn-sm" data-bs-toggle="modal" 
                                        data-bs-target={"#changeLogModalPlanningParametersId"} onClick={()=>{
                                            if(changeLogDataFetched){
                                                fetchChangeLogData();
                                            }
                                        }}>
                                        Change Log
                            </button>
                        </div>
                        <EditableGridActions
                            tabId={PROMOTION_PLANNING_TAB_ID}
                            disableIf={{
                                resetBtn: !changeTrackState,
                                saveBtn: !changeTrackState,
                                deleteRowBtn: !enableDelButton,
                                reCalculateBtn: !changeTrackState,
                            }}
                            onClick={{
                                resetBtn: resetCellEditsHandler,
                                saveBtn: (e:MouseEventHandler<HTMLButtonElement>, data:any) => saveCellEditsHandler(e, data),
                                addRowBtn: addRowHandler,
                                deleteRowBtn: (e:MouseEventHandler<HTMLButtonElement>, data:any) => deleteRowHandler(e, data),
                                reCalculateBtn: recalculateHandler,
                            }}
                            hideButtons={{
                                reCalculateBtn: false,
                                addRowBtn: false,
                                deleteRowBtn: false
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    rowSelection="multiple"
                    singleClickEdit={true}
                    onCellEditingStopped={cellValueChangeHandler}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        rowHeight: 22,
                        suppressColumnVirtualisation: true,
                        autoSizeStrategy: {
                            type: "fitCellContents",
                            skipHeader: true,
                            colIds: gridColumns.slice(6).map(col => col.field)
                        },
                        onSelectionChanged: rowSelectionHandler,
                        getRowStyle: (params: any) => newGridRowStyleHandler(params, "id"),
                        suppressMenuHide: true
                    }}
                    defaultColDef={{
                        ...AG_GRID_DEFAULT_COL_DEF,
                        wrapText: true,
                        autoHeight: true,
                    }}
                    modules={[
                        ...AG_GRID_MODULES
                    ]}
                />
            </div>
            {
                createPortal(
                    <Modal
                        id={"changeLogModalPlanningParametersId"}
                        ref={modalCloseBtnRef}
                        title={""}
                        onModalClose={() => { }}
                        width="xl"
                    >
                        <ChangeLogTable changeLogData={changeLogData}/>
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default PromotionPlanningGrid;
